import { LoadingIcon } from '@/components/icons';
import BottomSheetModal from '@/components/modules/modals/BottomSheetModal';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import AddCardForm from '@/components/templates/adyen/AddCardForm';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { classnames } from '@/helpers';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { SelectedPaymentMethod } from '@/types/checkout';
import '@adyen/adyen-web/dist/adyen.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAdyenCheckout } from '../hooks/useAdyenCheckout';
import { usePaymentMethodsContext } from '../hooks/usePaymentMethods';
import { SessionState } from '../payments.types';
import { AdyenPaymentResult, KlarnaVariant } from '../types/adyen.types';
import { getDisclaimerLabel, getModalTitle, getPaymentMethodApiType } from '../utils/paymentConfig.utils';

export interface AdyenCheckoutComponentProps {
  googlePayLineItems?: google.payments.api.DisplayItem[];
  submitting: boolean;
  /**
   * The Adyen session object obtained from the backend payment initiation
   */
  session?: SessionState;
  /**
   * Amount in minor units (e.g., cents)
   */
  amount: number;
  isOpen?: boolean;
  /**
   * Payment method type to render (should match what was sent to payment initiation)
   * Use 'scheme' or 'card' for credit/debit cards
   */
  paymentMethodType?: SelectedPaymentMethod['type'];
  /** For Klarna: selects 'klarna' (faktura) or 'klarna_account' (delbetala). Defaults to 'klarna'. */
  klarnaVariant?: KlarnaVariant;
  onClose: () => void;
  /** If provided, called instead of onClose when an Adyen SDK error occurs. Caller is responsible for closing the modal. */
  onError?: () => void;
  onComplete?: (result: AdyenPaymentResult) => void;
}

export const AdyenCheckoutComponent = ({
  session,
  paymentMethodType,
  klarnaVariant,
  onComplete,
  onClose,
  onError,
  isOpen,
  submitting,
  amount,
  googlePayLineItems,
}: AdyenCheckoutComponentProps) => {
  const { isMobileView } = useMobileView();
  const user = useAppSelector((state) => state.users?.user);
  const { paymentMethods } = usePaymentMethodsContext();

  const brands = useMemo(() => {
    if (paymentMethodType == null) return undefined;
    const apiType = getPaymentMethodApiType(paymentMethodType);
    return apiType ? paymentMethods.find((pm) => pm.type === apiType)?.brands ?? undefined : undefined;
  }, [paymentMethods, paymentMethodType]);

  // Custom hook handles all checkout initialization and lifecycle
  const {
    checkout,
    loading,
    submitting: adyenSubmitting,
    error,
    sessionState,
    paymentContainerRef,
    handleSubmit,
  } = useAdyenCheckout({
    session,
    paymentMethodType,
    klarnaVariant,
    amount,
    googlePayLineItems,
    brands,
    onComplete,
    onError: onError ?? onClose,
  });

  // Track whether Adyen has rendered visible content into the payment container.
  // This lets Adyen retain full authority over what's displayed — we only reveal
  // the container once it has a non-zero scroll height (i.e. actual visible content).
  // Some methods (e.g. Swish, Klarna) render nothing visible before triggering the
  // native sheet or redirect, so the loading overlay stays visible instead of showing
  // an empty modal. Wallet methods (Google Pay / Apple Pay) render their own pay button,
  // which the same scroll-height check reveals once it has mounted.
  const [hasVisibleContent, setHasVisibleContent] = useState(false);
  const containerDivRef = useRef<HTMLDivElement | null>(null);

  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerDivRef.current = node;
      paymentContainerRef(node);
    },
    [paymentContainerRef],
  );

  useEffect(() => {
    setHasVisibleContent(false);
    if (loading || !containerDivRef.current) return;

    const container = containerDivRef.current;

    if (container.scrollHeight > 0) {
      setHasVisibleContent(true);
      return;
    }

    const observer = new ResizeObserver(() => {
      if (container.scrollHeight > 0) {
        setHasVisibleContent(true);
        observer.disconnect();
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [loading]);

  const isNewCard = paymentMethodType === CHECKOUT_PAYMENT_METHOD.NEW_COF;
  const isApplePay = paymentMethodType === CHECKOUT_PAYMENT_METHOD.APPLE_PAY;
  const isGooglePay = paymentMethodType === CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY;
  const title = getModalTitle(paymentMethodType, !!user);

  const isSwish = paymentMethodType === CHECKOUT_PAYMENT_METHOD.SWISH;
  const showLoadingOverlay = loading || (submitting && !isSwish) || !hasVisibleContent;

  const content = isNewCard ? (
    <div className="p-3xl min-h-0 flex-1 overflow-y-auto">
      <AddCardForm
        checkout={checkout}
        submitLabel={user ? _t('addCard') : _t('pay')}
        disclaimerLabel={getDisclaimerLabel(!!user)}
        handleSubmit={handleSubmit}
        loading={loading}
        sessionState={adyenSubmitting ? 'hidden' : submitting ? 'disabled' : sessionState}
        paymentContainer={paymentContainerRef}
        error={error}
        detachedSubmit={false}
        inModal={false}
      />
    </div>
  ) : isApplePay || isGooglePay ? (
    <div className="relative min-h-[220px]">
      {(loading || !hasVisibleContent) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingIcon className="w-3xl" />
        </div>
      )}
      <div
        className={classnames(
          'gap-xxl p-3xl flex flex-col items-center',
          (loading || !hasVisibleContent) && 'invisible',
        )}
      >
        <img
          src={isApplePay ? '/images/logos/applepay-logo.svg' : '/images/logos/googlepay-logo.svg'}
          alt={isApplePay ? 'Apple Pay' : 'Google Pay'}
          className="w-[95px]"
        />
        <p className="text-fg_secondary text-center text-sm">
          {isApplePay ? _t('applePayModalDescription') : _t('googlePayModalDescription')}
        </p>
        <div
          id="payment-container"
          className="w-full [&>*]:!block [&>*]:!min-h-[56px] [&>*]:!w-full"
          ref={combinedRef}
        />
      </div>
    </div>
  ) : (
    <div className="relative flex min-h-[200px] w-full flex-1 flex-col overflow-y-auto rounded-b-lg bg-white p-3xl">
      {showLoadingOverlay && (
        <div className="gap-md absolute inset-0 z-10 flex flex-col items-center justify-center">
          <LoadingIcon className="w-3xl" />
          {!isSwish && <p className="text-fg_secondary text-sm">{_t('completePaymentInWindow')}</p>}
        </div>
      )}
      <div
        id="payment-container"
        className={classnames(
          'w-full transition-opacity duration-200',
          showLoadingOverlay ? 'opacity-0' : 'opacity-100',
        )}
        ref={combinedRef}
      />
    </div>
  );

  if (isMobileView) {
    return (
      <BottomSheetModal isOpen={isOpen ?? false} onClose={onClose} title={title}>
        {content}
      </BottomSheetModal>
    );
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose}>
      <Modal.Content newDesign={true} contentPadding={false} floating={true} contentMaxWidth={500}>
        <Modal.Header newDesign={true} floating={true} title={title} onClose={onClose} />
        {content}
      </Modal.Content>
    </Modal>
  );
};
